import { ApiError, demoApi } from "@/api/client";
import { withDrainLock, DRAIN_LOCKS } from "./drainLock";
import type { CreateEntryRequest } from "@/api/types";
import { outbox } from "./outbox";
import { mappingQueue } from "./mappingQueue";
import { createPersistentQueue, type PersistentQueue, type QueueItem } from "./persistentQueue";
import { registerDrainTriggers } from "./drainTriggers";
import { isPlausibleReceiptDate } from "@/lib/receiptDate";

/**
 * A receipt photo captured while offline, held for replay (build tasks
 * #1/#2, `docs/demo/tradeshow-2026-09.md` Stretch A + the "operator journey").
 *
 * PRIVACY: this is the ONLY place a receipt photo is retained anywhere in
 * this app, and only until it is read. `drain()` deletes it the moment the
 * read (successful or not) no longer needs it — see the privacy-tension note
 * in `components/ReceiptScan.tsx` and the return notes this shipped with.
 */
export interface QueuedScan extends QueueItem {
  blob: Blob;
  fileName: string;
  /**
   * Captured at enqueue time, not resolved at drain time — `useReferenceData`
   * only re-resolves this on mount, and a photo taken offline may drain long
   * after the tab that captured it. The value itself is safe to capture
   * early: it was already fetched online, before the phone went offline.
   */
  entityId: string;
}

const DB_NAME = "easyfarm-offline-scans";
const STORE_NAME = "scans";

function newClientId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

type Listener = (items: QueuedScan[]) => void;

class ScanQueueManager {
  private storePromise: Promise<PersistentQueue<QueuedScan>> | null = null;
  private listeners = new Set<Listener>();
  private draining = false;

  private getStore(): Promise<PersistentQueue<QueuedScan>> {
    if (!this.storePromise) {
      this.storePromise = createPersistentQueue<QueuedScan>(DB_NAME, STORE_NAME, {
        clientId: "__probe__",
        blob: new Blob([""], { type: "text/plain" }),
        fileName: "probe",
        entityId: "",
        queuedAt: 0,
      });
    }
    return this.storePromise;
  }

  async list(): Promise<QueuedScan[]> {
    const store = await this.getStore();
    return store.all();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private async notify(): Promise<void> {
    const items = await this.list();
    for (const fn of this.listeners) fn(items);
  }

  /** Hold a captured photo because reading it right now failed offline (build task #1). */
  async enqueue(blob: Blob, fileName: string, entityId: string): Promise<void> {
    const store = await this.getStore();
    await store.add({ clientId: newClientId(), blob, fileName, entityId, queuedAt: Date.now() });
    await this.notify();
  }

  /**
   * Replicate on reconnect (build task #2): read the held photo, then create
   * the entry from whatever it found — there is no visitor present at
   * reconnect time to correct a guess, so this only ever posts a COMPLETE
   * read (vendor + account + a positive amount). An incomplete read is never
   * turned into a bad entry; the photo stays queued rather than being
   * silently discarded — see the return notes for this scope's honest limit
   * (no in-app "finish this by hand" flow exists yet).
   *
   * Sequential and in order, matching `outbox.ts`'s `drain` exactly, for the
   * same reason: "in order" has to be actually true, not merely likely.
   */
  async drain(): Promise<void> {
    if (this.draining) return;
    this.draining = true;
    try {
      // ACROSS TABS. `this.draining` is one instance's flag — see offline/drainLock.ts.
      // MEASURED 2026-09-08 without it: two tabs, one held receipt, and the reconnect
      // produced TWO entries AND TWO stored photographs off a single scan.
      await withDrainLock(DRAIN_LOCKS.scans, async () => {
        try {
          await this.drainOnce();
        } finally {
          // A tab queued behind the lock may find the store already emptied by the other tab and
          // process nothing (or bail still-offline); without this its subscribers keep a stale view.
          await this.notify();
        }
      });
    } finally {
      this.draining = false;
    }
  }

  /** One pass over the queue. Only ever called while the origin-wide scan lock is held. */
  private async drainOnce(): Promise<void> {
    {
      const store = await this.getStore();
      const items = await store.all();
      for (const item of items) {
        let analysis: Awaited<ReturnType<typeof demoApi.analyzeReceipt>>;
        try {
          analysis = await demoApi.analyzeReceipt(item.blob, undefined, item.fileName);
        } catch (err) {
          if (err instanceof ApiError && err.status === 0) return; // still offline — stop, in order
          console.warn("A queued receipt photo could not be read; leaving it queued.", err);
          continue;
        }

        if (!analysis.vendorId || !analysis.accountId || !analysis.amount || analysis.amount <= 0) {
          console.warn(
            "A queued receipt photo was read but the analysis was incomplete " +
              "(missing vendor, category, or amount) — leaving it queued rather than " +
              "posting a guessed entry.",
          );
          continue;
        }

        const payload: CreateEntryRequest = {
          entityId: item.entityId,
          vendorId: analysis.vendorId,
          accountId: analysis.accountId,
          amount: analysis.amount,
          // ef-006. The queued-scan path matters MORE than the live one here: a photo
          // taken with no signal is exactly the case where keeping the document is the
          // whole value, because the reading may well have been poor.
          ...(analysis.receiptImageId ? { receiptImageId: analysis.receiptImageId } : {}),
          // AND IT IS STILL A RECEIPT, AND IT STILL HAPPENED ON ITS OWN DATE.
          //
          // This path had the photo and neither of the other two. Measured 2026-09-07 by
          // photographing the fixture in airplane mode and letting it drain:
          //
          //     amount=461.58  source=manual  occurred_on=2026-09-07  receipt=yes
          //
          // The receipt reads 2026-08-26. So the airplane-mode beat — the one the cut list
          // never cuts — filed its entry as MANUAL, on the wrong day, while the live path
          // put "Dated Aug 26, 2026 — it will book on that day" on screen a moment earlier.
          // Two surfaces of one product disagreeing about the same photograph.
          //
          // `isPlausibleReceiptDate` gates the date here exactly as QuickEntry and
          // ReceiptCapture gate it: the server refuses a future or years-old date, and a
          // model that misread the year should not turn into a rejected drain.
          source: "Receipt",
          ...(isPlausibleReceiptDate(analysis.date) ? { occurredOn: analysis.date! } : {}),
        };

        try {
          await demoApi.createEntry(payload);
        } catch (err) {
          if (err instanceof ApiError && err.status === 0) {
            // Offline again mid-drain. The read already happened — hand the
            // finished entry to the entry outbox rather than re-reading the
            // photo (and re-spending a model call) on the next reconnect.
            await outbox.enqueue(payload, {
              vendorId: analysis.vendorId,
              accountId: analysis.accountId,
            });
            await store.remove(item.clientId);
            await this.notify();
            return;
          }
          console.warn("A queued receipt's entry was rejected by the server; leaving the photo queued.", err);
          continue;
        }

        // THE MAPPING LESSON (build task #3) — best-effort like the online
        // path (QuickEntry.tsx), but never silently lost if it's offline:
        // queued the same way the entry itself was.
        try {
          await demoApi.confirmMapping({ vendorId: analysis.vendorId, accountId: analysis.accountId });
        } catch (err) {
          if (err instanceof ApiError && err.status === 0) {
            await mappingQueue.enqueue({ vendorId: analysis.vendorId, accountId: analysis.accountId });
          } else {
            console.warn("Entry created, but the vendor mapping was not recorded.", err);
          }
        }

        // The read succeeded and the entry is safely posted — this is the
        // moment the photo is no longer needed. Deleted, not archived.
        await store.remove(item.clientId);
        await this.notify();
      }
    }
  }
}

export const scanQueue = new ScanQueueManager();

registerDrainTriggers(scanQueue);
